<template>
  <div class="container" :class="`container--${type}`" :data-test-id="`${type}-text-box`">
    <div v-if="title || closeButton" class="title">
      <slot name="icon" />
      <template v-if="title"> {{ title }}</template>

      <div class="action-header" v-if="closeButton || hasSlot.actionHeader">
        <slot name="actionHeader">
          <Btn
            v-if="closeButton"
            @click="$emit('close')"
            class="close-button"
            icon
            color="ghost"
            data-test-id="text-box__close-button"
          >
            <CloseIcon16 />
          </Btn>
        </slot>
      </div>
    </div>
    <div class="text">{{ text }}</div>

    <div class="action-footer" v-if="hasSlot.actionFooter">
      <slot name="actionFooter" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import CloseIcon16 from '@components/Icons/CloseIcon16.vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  emits: { close: null },
  name: 'TextBox',

  components: {
    CloseIcon16,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: null,
    },
    text: {
      type: String,
      required: true,
    },
    type: {
      type: String as PropType<'error' | 'info' | 'alert'>,
      required: false,
      default: 'info',
    },
    closeButton: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  computed: {
    hasSlot() {
      return {
        actionFooter: hasSlotContent(this.$slots.actionFooter, {}),
        actionHeader: hasSlotContent(this.$slots.actionHeader, {}),
      };
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';

.container {
  display: flex;
  flex-direction: column;
  border-radius: settings.$border-radius;
  padding: 16px;
  text-align: left;
  gap: 8px;

  &--error,
  &--alert {
    background: colors.$color-red-50;
    border: 1px solid colors.$color-red-200;
  }

  &--error {
    background-image: linear-gradient(to top, colors.$color-red-600 3px, transparent 0);
  }

  &--info {
    background: colors.$color-blue-50;
    border: 1px solid colors.$color-blue-200;
  }

  a {
    text-decoration: underline;
  }
}

.title {
  @include typography.typography-15-medium(24px);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}

.action-header {
  margin-left: auto;
}

.action-footer {
  padding-top: 8px;
}

.text {
  @include typography.typography-15-normal(24px);
}
</style>
